import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PortalStatus from '@/components/portal/PortalStatus';
import Panel from '@/components/ui/Panel';
import Toast from '@/components/ui/Toast';
import { aed, shortDate } from '@/lib/format';
import { portalInvoices, portalOrders, type PortalInvoice } from '@/mocks/portal';

export default function PortalFinancePage() {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState<PortalInvoice[]>(portalInvoices);
  const [open, setOpen] = useState(false);
  const [po, setPo] = useState(portalOrders[0].po);
  const [amount, setAmount] = useState('');
  const [toast, setToast] = useState<{ message: string; detail?: string } | null>(null);

  const paid = invoices.filter((i) => i.status === 'Paid').reduce((s, i) => s + i.amount, 0);
  const awaiting = invoices.filter((i) => i.status === 'AwaitingPayment').reduce((s, i) => s + i.amount, 0);
  const next = invoices
    .filter((i) => i.status === 'AwaitingPayment')
    .sort((a, b) => a.due.localeCompare(b.due))[0];

  const submitInvoice = () => {
    const value = Number(amount) || 0;
    if (value <= 0) return;
    setInvoices((prev) => [
      {
        invoice: `INV-2026-040${prev.length + 1}`,
        po,
        amount: value,
        status: 'AwaitingPayment',
        due: '2026-11-20',
      },
      ...prev,
    ]);
    setOpen(false);
    setAmount('');
    setToast({ message: t('portal.finance.invoiceToast'), detail: `${po} · ${aed(value)}` });
  };

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 border-b border-background-200 pb-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-[20px] font-semibold leading-6 text-foreground-950">{t('portal.finance.title')}</h1>
          <p className="mt-1 max-w-2xl text-[12.5px] leading-5 text-foreground-500">{t('portal.finance.subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 hover:bg-primary-600"
          >
            <i className="ri-upload-2-line text-[13px]" />
            {t('portal.finance.uploadInvoice')}
          </button>
          <button
            type="button"
            onClick={() => setToast({ message: t('portal.finance.statementToast') })}
            className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 text-[12px] font-medium text-foreground-700 hover:bg-background-100"
          >
            <i className="ri-download-2-line text-[13px]" />
            {t('portal.finance.downloadStatement')}
          </button>
        </div>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
        {[
          {
            label: t('portal.finance.paidMonth'),
            value: aed(paid),
            hint: t('portal.finance.paidMonthHint'),
            icon: 'ri-checkbox-circle-line',
            tone: 'bg-emerald-50 text-emerald-700',
            valueTone: 'text-foreground-950',
          },
          {
            label: t('portal.finance.awaiting'),
            value: aed(awaiting),
            hint: t('portal.finance.awaitingHint'),
            icon: 'ri-time-line',
            tone: 'bg-accent-50 text-accent-700',
            valueTone: 'text-accent-800',
          },
          {
            label: t('portal.finance.next'),
            value: next ? aed(next.amount) : aed(0),
            hint: next ? `${shortDate(next.due)} · ${t('portal.finance.nextHint')}` : t('portal.finance.nextHint'),
            icon: 'ri-calendar-line',
            tone: 'bg-primary-50 text-primary-700',
            valueTone: 'text-foreground-950',
          },
        ].map((c) => (
          <div key={c.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
            <div className="flex items-start justify-between gap-2">
              <p className="text-[10.5px] font-medium uppercase tracking-[0.1em] text-foreground-500">{c.label}</p>
              <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded ${c.tone}`}>
                <i className={`${c.icon} text-[13px]`} />
              </span>
            </div>
            <p className={`tnum mt-2 text-[21px] font-semibold leading-7 ${c.valueTone}`}>{c.value}</p>
            <p className="mt-0.5 text-[10.5px] leading-4 text-foreground-400">{c.hint}</p>
          </div>
        ))}
      </div>

      <div className="mb-4 flex items-start gap-2.5 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded bg-primary-100 text-primary-700">
          <i className="ri-information-line text-[13px]" />
        </span>
        <div className="min-w-0">
          <p className="text-[12px] font-semibold text-primary-900">{t('portal.finance.bannerTitle')}</p>
          <p className="mt-0.5 text-[11.5px] leading-4 text-primary-900/80">{t('portal.finance.bannerBody')}</p>
        </div>
      </div>

      <Panel padded={false} className="hidden lg:block">
        <div className="border-b border-background-200 px-4 py-3">
          <h2 className="text-[13px] font-semibold text-foreground-950">{t('portal.finance.invoicesTitle')}</h2>
          <p className="mt-0.5 text-[11px] text-foreground-500">{t('portal.finance.invoicesSub')}</p>
        </div>
        <div className="overflow-x-auto thin-scroll">
          <table className="w-full min-w-[720px] border-collapse text-[12px]">
            <thead>
              <tr className="bg-background-100/80 text-left text-[10px] uppercase tracking-[0.08em] text-foreground-500">
                <th className="px-4 py-2.5 font-medium">{t('portal.finance.colInvoice')}</th>
                <th className="px-3 py-2.5 font-medium">{t('portal.finance.colPo')}</th>
                <th className="px-3 py-2.5 text-right font-medium">{t('portal.finance.colAmount')}</th>
                <th className="px-3 py-2.5 font-medium">{t('portal.finance.colStatus')}</th>
                <th className="px-4 py-2.5 font-medium">{t('portal.finance.colDue')}</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((i) => (
                <tr key={i.invoice} className="border-t border-background-200/70 hover:bg-background-100/40">
                  <td className="px-4 py-2.5">
                    <p className="tnum text-[11.5px] font-semibold text-foreground-950">{i.invoice}</p>
                  </td>
                  <td className="tnum px-3 py-2.5 text-[11px] text-primary-700">{i.po}</td>
                  <td className="tnum px-3 py-2.5 text-right font-semibold text-foreground-900">{aed(i.amount)}</td>
                  <td className="px-3 py-2.5">
                    <PortalStatus status={i.status} />
                  </td>
                  <td className="tnum px-4 py-2.5 text-[11px] text-foreground-600">{shortDate(i.due)}</td>
                </tr>
              ))}
              {invoices.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-[12px] text-foreground-500">
                    {t('portal.finance.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="border-t border-background-200 px-4 py-2.5 text-[10.5px] text-foreground-400">
          {t('portal.finance.note')}
        </p>
      </Panel>

      <div className="flex flex-col gap-2.5 lg:hidden">
        {invoices.map((i) => (
          <div key={i.invoice} className="rounded-lg border border-background-200 bg-background-50 p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="tnum text-[12px] font-semibold text-foreground-950">{i.invoice}</p>
                <p className="tnum text-[10.5px] text-primary-700">{i.po}</p>
              </div>
              <PortalStatus status={i.status} className="!px-1.5 !text-[10px]" />
            </div>
            <div className="mt-2 flex items-center justify-between border-t border-background-200 pt-2">
              <span className="text-[10.5px] text-foreground-500">
                {t('portal.finance.colDue')} {shortDate(i.due)}
              </span>
              <span className="tnum text-[13px] font-semibold text-foreground-950">{aed(i.amount)}</span>
            </div>
          </div>
        ))}
        {invoices.length === 0 && (
          <p className="rounded-lg border border-background-200 bg-background-50 px-3 py-8 text-center text-[12px] text-foreground-500">
            {t('portal.finance.empty')}
          </p>
        )}
        <p className="mt-1 text-[10.5px] leading-4 text-foreground-400">{t('portal.finance.note')}</p>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-950/30 p-4">
          <div className="w-full max-w-md rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="text-[14px] font-semibold text-foreground-950">{t('portal.finance.invoiceTitle')}</h2>
            <p className="mt-1 text-[11.5px] leading-4 text-foreground-500">{t('portal.finance.invoiceSub')}</p>
            <div className="mt-3 flex flex-col gap-3">
              <label className="block">
                <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('portal.finance.invoicePo')}</span>
                <select
                  value={po}
                  onChange={(e) => setPo(e.target.value)}
                  className="h-9 w-full rounded-md border border-background-300 bg-background-50 px-2 text-[12px] outline-none focus:border-primary-400"
                >
                  {portalOrders.map((o) => (
                    <option key={o.po} value={o.po}>
                      {o.po} · {aed(o.lines.reduce((s, l) => s + l.qty * l.price, 0))}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-[11px] font-medium text-foreground-600">{t('portal.finance.invoiceAmount')}</span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="25575"
                  className="tnum h-9 w-full rounded-md border border-background-300 bg-background-50 px-2.5 text-[12px] outline-none focus:border-primary-400"
                />
              </label>
              <label className="flex cursor-pointer flex-col items-center justify-center rounded-md border border-dashed border-background-300 bg-background-100 px-3 py-4 text-center">
                <i className="ri-file-pdf-2-line text-[19px] text-foreground-400" />
                <span className="mt-1 text-[11px] text-foreground-500">{t('portal.order.uploadNote')}</span>
                <input type="file" accept=".pdf,image/*" className="hidden" />
              </label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="h-8 cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-3 text-[12px] font-medium text-foreground-700 hover:bg-background-100"
              >
                {t('portal.common.cancel')}
              </button>
              <button
                type="button"
                onClick={submitInvoice}
                className="h-8 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-3 text-[12px] font-semibold text-background-50 hover:bg-primary-600"
              >
                {t('portal.finance.submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} detail={toast.detail} onClose={() => setToast(null)} />}
    </div>
  );
}